import { useState } from "react";
import { EyeIcon, ListFilterIcon } from "lucide-react";

import { TriageQueueEnum } from "@/__generated__/graphql";
import { Icon } from "@/components/atoms/Icon";
import { Popper } from "@/components/atoms/Popper";
import { List } from "@/components/organisms/List";
import { Queries } from "@/constants";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { State } from "@/state";
import type { TriageRowPart } from "@/state/@types";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { TRIAGE_TOOLBAR_BUTTON_CLASS } from "./TriageDetailActions";
import { QUEUE_LABELS } from "./TriageView.keys";

import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";

/**
 * The queues picked, each with whether the payments shown are in it (true) or
 * kept out of it (false): the queue shows the payments that are so for every
 * one at once.
 */
export type TriageQueuePicks = Partial<Record<TriageQueueEnum, boolean>>;

/** Every queue, in the order the filter offers them. */
const QUEUES = Object.keys(QUEUE_LABELS) as TriageQueueEnum[];

/** One of Similar's own filters - what the payment itself is - offered in the Filter ahead of the queues. */
export type TriageLikeFilter = {
  key: string;
  /** The payment's own value, as the filter narrows to it: "Money out". */
  label: string;
  /** What kind of filter it is, said beside it: "Direction". */
  kind: string;
  isOn: boolean;
};

/** A row of the Filter: one of Similar's own, a queue - or the line between the two. */
type TriageFilterRow =
  | {
      key: string;
      like: TriageLikeFilter;
      queue?: undefined;
      isDivider?: undefined;
    }
  | {
      key: string;
      queue: TriageQueueEnum;
      like?: undefined;
      isDivider?: undefined;
    }
  | { key: string; isDivider: true; like?: undefined; queue?: undefined };

/**
 * The queue's filters, on its bar - and, Similar on, where they were,
 * narrowing Similar's list as they narrow the queue: one Filter, its icon
 * alone, opening every filter there is - each state a payment can be in, and,
 * Similar on, its own filters (*like*), what the payment itself is, ahead of
 * them - and counting, in its corner, every one picked.
 */
export function TriageFilterGroup({
  value,
  onChange,
  entityIdentifiers,
  like = null,
}: {
  value: TriageQueuePicks;
  onChange: (queues: TriageQueuePicks) => void;
  /** The businesses the queue is of, for the counts. */
  entityIdentifiers: string[];
  /** Similar on: its own filters, and how each is turned on and off - or all at once. */
  like?: {
    filters: TriageLikeFilter[];
    onToggle: (key: string) => void;
    onClear: () => void;
  } | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  // Counted when asked for: each queue is a count of its own.
  const { data, loading } = useBackendQuery(Queries.TRIAGE_QUEUE_COUNTS_QUERY, {
    variables: { entityIdentifiers },
    skip: !isOpen,
    fetchPolicy: "network-only",
  });
  const isLoading = loading || data === undefined;
  const counts = new Map(
    (data?.triageQueueCounts?.data ?? []).map((row) => [row.queue, row.count]),
  );
  // Every one picked, Similar's own and the states alike.
  const count =
    (like?.filters ?? []).filter((filter) => filter.isOn).length +
    Object.keys(value).length;
  const label = count === 0 ? "Filter" : `Filters (${count})`;
  const toggle = (queue: TriageQueueEnum) => {
    const { [queue]: _, ...rest } = value;
    onChange(value[queue] !== undefined ? rest : { ...value, [queue]: true });
  };
  const rows: TriageFilterRow[] = [
    ...(like?.filters ?? []).map((filter) => ({
      key: `like-${filter.key}`,
      like: filter,
    })),
    // Similar's own apart from the queues: a line between, no row's own.
    ...(like ? [{ key: "divider", isDivider: true as const }] : []),
    ...QUEUES.map((queue) => ({ key: queue, queue })),
  ];
  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      // Opened by its Filter, a button of its own.
      isClickable={false}
      // First in its bar: opened from its left, under it.
      placement="bottom-start"
      floatingStyle={{ width: 300 }}
      style={{ flexShrink: 0 }}
      anchor={
        <div data-testid="triage-filter-group" className="flex shrink-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                data-on={count > 0 || isOpen || undefined}
                aria-label={label}
                aria-expanded={isOpen}
                data-testid="triage-queue-filter"
                onClick={() => setIsOpen(!isOpen)}
                // What its count is set in the corner of.
                className={cn(TRIAGE_TOOLBAR_BUTTON_CLASS, "relative")}
              >
                <Icon ReactIcon={ListFilterIcon} size="sm" />
                {count > 0 ? (
                  <span className="absolute top-0.5 right-0.5 text-[10px] leading-none [font-variant-numeric:tabular-nums]">
                    {count}
                  </span>
                ) : null}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{label}</TooltipContent>
          </Tooltip>
        </div>
      }
    >
      <div
        data-testid="triage-queue-filter-panel"
        className="flex flex-col min-h-0"
      >
        <List<TriageFilterRow>
          data={rows}
          getRowKey={(row) => row.key}
          getShouldSkipRow={(row) => row.isDivider === true}
          onItemClick={(row) => {
            if (row.like) like?.onToggle(row.like.key);
            else if (row.queue) toggle(row.queue);
          }}
          autofocus
          fillHeight={false}
          size="sm"
        >
          <List.Items<TriageFilterRow>>
            {(row: TriageFilterRow) =>
              row.isDivider ? (
                <List.Item value={row}>
                  {/* Across the panel, as a menu's separator is. */}
                  <Separator className="-mx-2 w-auto" />
                </List.Item>
              ) : (
                <List.Item
                  value={row}
                  data-testid={
                    row.like
                      ? `triage-similar-filter-${row.like.key}`
                      : `triage-queue-filter-${row.queue}`
                  }
                >
                  <div className="flex items-center gap-2 w-full min-h-6 pl-1 pr-1">
                    <Checkbox
                      checked={
                        row.like
                          ? row.like.isOn
                          : value[row.queue] !== undefined
                      }
                      className="pointer-events-none"
                      tabIndex={-1}
                    />
                    <Typography
                      level="body-md"
                      className="truncate flex-1 min-w-0"
                    >
                      {row.like ? row.like.label : QUEUE_LABELS[row.queue]}
                    </Typography>
                    <Typography
                      level="body-sm"
                      className="truncate text-muted-foreground/80"
                    >
                      {row.like ? (
                        row.like.kind
                      ) : (
                        <Skeleton loading={isLoading} variant="inline">
                          {isLoading ? "00" : (counts.get(row.queue) ?? 0)}
                        </Skeleton>
                      )}
                    </Typography>
                    {row.queue && value[row.queue] !== undefined ? (
                      <Switch
                        checked={value[row.queue]}
                        data-testid={`triage-queue-filter-${row.queue}-switch`}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={(event) => event.stopPropagation()}
                        onCheckedChange={(checked) =>
                          onChange({
                            ...value,
                            [row.queue]: checked === true,
                          })
                        }
                        aria-label={
                          value[row.queue]
                            ? `Showing those in ${QUEUE_LABELS[row.queue]}`
                            : `Keeping out those in ${QUEUE_LABELS[row.queue]}`
                        }
                      />
                    ) : null}
                  </div>
                </List.Item>
              )
            }
          </List.Items>
        </List>
        {count > 0 ? (
          <div className="flex justify-end p-2 [border-top-width:1px] [border-top-style:solid] border-border">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange({});
                like?.onClear();
              }}
              data-testid="triage-queue-filter-clear"
            >
              Clear
            </Button>
          </div>
        ) : null}
      </div>
    </Popper>
  );
}

/**
 * What a queue's row may draw, to show or hide - or the rows drawn mirrored,
 * or the line between the two.
 */
type TriageQueueViewRow =
  | { part: TriageRowPart | "mirrored"; label: string }
  | { part: "divider"; isDivider: true };

const VIEW_ROWS: TriageQueueViewRow[] = [
  { part: "tools", label: "Tool row" },
  { part: "tags", label: "Tags" },
  { part: "documents", label: "Documents" },
  { part: "businesses", label: "Other business" },
  // The rows - and what a match lines up beside them - drawn facing the
  // suggestions, in Similar or not: apart from the parts, a line between.
  { part: "divider", isDivider: true },
  { part: "mirrored", label: "Mirrored" },
];

/**
 * How the queue's rows are drawn, at the bar's end: its eye opens the parts a
 * row may show - its line of tools, and on it its tags, its documents, the
 * other business it is booked in - each ticked while shown, and under them
 * the rows mirrored, ticked while they are. The line hidden, what is on it is
 * hidden with it: those can't be ticked till it is back. The person's choice,
 * kept.
 */
export function TriageQueueView() {
  const [isOpen, setIsOpen] = useState(false);
  const hidden = State((state) => state.hiddenTriageRowParts);
  const setHidden = State((state) => state.setHiddenTriageRowParts);
  const isMirrored = State((state) => state.isTriageMirrored);
  // On the line of tools, the line hidden: hidden with it.
  const isUnderHiddenTools = (row: TriageQueueViewRow) =>
    row.part !== "tools" && row.part !== "mirrored" && hidden.includes("tools");

  return (
    <Popper
      isOpenState={[isOpen, setIsOpen]}
      // Opened by its eye, a button of its own.
      isClickable={false}
      // Last in the queue's bar: opened from its right, under it.
      placement="bottom-end"
      floatingStyle={{ width: 220 }}
      style={{ flexShrink: 0 }}
      anchor={
        // On while the rows are drawn other than as they come - a part
        // hidden, or mirrored - as the Filter is while one is set.
        <Button
          variant="ghost"
          size="icon-sm"
          data-on={hidden.length > 0 || isMirrored || isOpen || undefined}
          aria-label="View"
          aria-expanded={isOpen}
          data-testid="triage-queue-view"
          onClick={() => setIsOpen(!isOpen)}
          className={TRIAGE_TOOLBAR_BUTTON_CLASS}
        >
          <Icon ReactIcon={EyeIcon} size="sm" />
        </Button>
      }
    >
      <div data-testid="triage-queue-view-panel">
        <List<TriageQueueViewRow>
          data={VIEW_ROWS}
          getRowKey={(row) => row.part}
          getShouldSkipRow={(row) =>
            row.part === "divider" || isUnderHiddenTools(row)
          }
          onItemClick={({ part }) => {
            if (part === "mirrored")
              State.getState().setTriageMirrored(!isMirrored);
            else if (part !== "divider")
              setHidden(
                hidden.includes(part)
                  ? hidden.filter((other) => other !== part)
                  : [...hidden, part],
              );
          }}
          autofocus
          fillHeight={false}
          size="sm"
        >
          <List.Items<TriageQueueViewRow>>
            {(row: TriageQueueViewRow) =>
              row.part === "divider" ? (
                <List.Item value={row}>
                  {/* Across the panel, as a menu's separator is. */}
                  <Separator className="-mx-2 w-auto" />
                </List.Item>
              ) : (
                <List.Item
                  value={row}
                  data-testid={`triage-queue-view-${row.part}`}
                >
                  <div
                    // On the line of tools: under it, set in.
                    className={cn(
                      "flex items-center gap-2 w-full min-h-6 pl-1 pr-1",
                      row.part === "tools" || row.part === "mirrored"
                        ? "pl-1"
                        : "pl-6",
                    )}
                  >
                    <Checkbox
                      checked={
                        row.part === "mirrored"
                          ? isMirrored
                          : !hidden.includes(row.part)
                      }
                      disabled={isUnderHiddenTools(row)}
                      className="pointer-events-none"
                      tabIndex={-1}
                    />
                    <Typography
                      level="body-md"
                      className={cn(
                        "truncate",
                        isUnderHiddenTools(row) && "text-muted-foreground/80",
                      )}
                    >
                      {row.label}
                    </Typography>
                  </div>
                </List.Item>
              )
            }
          </List.Items>
        </List>
      </div>
    </Popper>
  );
}
